import type {
  OperationNodeData,
  GraphSnapshotLight,
  StepTranslation,
  TranslationSegment,
  CheckpointDef,
  StepIconState,
  TableColumn,
  TableRow,
} from './translation.types';
import type { OperationTypeDefinition, ParameterDefinition, Checkpoint } from '@/graph/domain/types';
import {
  buildScalarParamSegments,
  isRequired,
} from './buildParamSegment';
import { normalizeTableColumns, pickGroupRows, pickRowArray, rowHasValue } from './translationParamUtils';

/**
 * Pure function that generates human-readable translation sentences
 * from graph operation nodes. No React, no maxGraph, no Zustand dependencies.
 */
export class TranslationGenerator {
  private static readonly debugTranslation = true;


  static generateAll(
    operationNodes: OperationNodeData[],
    graph: GraphSnapshotLight,
    editable: boolean,
    catalog: Map<string, OperationTypeDefinition>,
  ): StepTranslation[] {
    // Group operations by groupId for compound rendering
    const groupedOps = new Map<string, OperationNodeData[]>();
    const standaloneOps: OperationNodeData[] = [];

    for (const node of operationNodes) {
      if (node.groupId && graph.groups.has(node.groupId)) {
        const list = groupedOps.get(node.groupId) ?? [];
        list.push(node);
        groupedOps.set(node.groupId, list);
      } else {
        standaloneOps.push(node);
      }
    }

    const results: StepTranslation[] = [];
    let stepCounter = 0;

    // Track which groups we've already emitted
    const emittedGroups = new Set<string>();

    for (const node of operationNodes) {
      if (node.groupId && graph.groups.has(node.groupId)) {
        if (emittedGroups.has(node.groupId)) continue;
        emittedGroups.add(node.groupId);
        stepCounter++;

        const group = graph.groups.get(node.groupId)!;
        const subNodes = (groupedOps.get(node.groupId) ?? []).sort(
          (a, b) => (a.orderInGroup ?? a.creationOrder) - (b.orderInGroup ?? b.creationOrder),
        );

        const subSteps = subNodes.map((sub, i) =>
          this.generateOne(sub, graph, editable, catalog, `${stepCounter}.${i + 1}`),
        );

        // Resolve all cycle infos from group, sorted by startIndex
        let cycleInfos: StepTranslation['cycleInfos'];
        if (group.cycleRanges && group.cycleRanges.length > 0) {
          cycleInfos = group.cycleRanges
            .sort((a, b) => a.startIndex - b.startIndex)
            .map((cycle) => ({
              id: cycle.id,
              count: cycle.iterationCount,
              range: `${stepCounter}.${cycle.startIndex + 1} → ${stepCounter}.${cycle.endIndex + 1}`,
              startIndex: cycle.startIndex,
              endIndex: cycle.endIndex,
            }));
        }

        // Build group-level segments
        const inputMats = this.resolveInputMaterials(node.id, graph);
        const groupSegments: TranslationSegment[] = [
          { kind: 'text', value: `Perform ${group.label} on ` },
          ...(inputMats.length > 0
            ? [inputMats[0]]
            : [{ kind: 'text' as const, value: 'target material' }]),
          { kind: 'text', value: ', with the following steps:' },
        ];

        results.push({
          nodeId: node.groupId,
          creationOrder: node.creationOrder,
          stepNumber: String(stepCounter),
          type: 'group',
          operationName: group.label,
          archetype: node.archetype,
          segments: groupSegments,
          subSteps,
          cycleInfos,
          checkpoints: [],
          icons: { hasNotes: false, hasApproval: false, hasNotifications: false },
        });
      } else {
        stepCounter++;
        results.push(this.generateOne(node, graph, editable, catalog, String(stepCounter)));
      }
    }

    return results;
  }

  static generateOne(
    node: OperationNodeData,
    graph: GraphSnapshotLight,
    editable: boolean,
    catalog: Map<string, OperationTypeDefinition>,
    stepNumber: string,
  ): StepTranslation {
    const typeDef = this.resolveTypeDef(node, catalog);
    const archetype = node.archetype;

    const segments = this.buildSegments(node, typeDef, graph, editable, archetype);

    return {
      nodeId: node.id,
      creationOrder: node.creationOrder,
      stepNumber,
      type: 'operation',
      operationName: typeDef?.operationName ?? node.displayName,
      archetype,
      segments,
      checkpoints: this.buildCheckpoints(node, editable),
      duration: this.resolveDuration(node),
      icons: this.buildIcons(node),
      notes: node.notes,
      comments: node.comments,
      dataExpected: node.dataExpected ?? false,
      approvers: node.approvers,
      notifications: node.notifications,
    };
  }

  private static resolveTypeDef(
    node: OperationNodeData,
    catalog: Map<string, OperationTypeDefinition>,
  ): OperationTypeDefinition | undefined {
    if (node.operationTypeId) {
      const byId = catalog.get(node.operationTypeId);
      if (byId) return byId;
    }

    const displayName = (node.displayName || '').trim().toLowerCase();
    if (!displayName) return undefined;

    for (const item of catalog.values()) {
      const opName = (item.operationName || '').trim().toLowerCase();
      if (opName !== displayName) continue;
      if (item.operationType && item.operationType !== node.archetype) continue;
      return item;
    }

    return undefined;
  }

  // ─── Segment Builders ──────────────────────────────────────────────────────

  private static buildSegments(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
    graph: GraphSnapshotLight,
    editable: boolean,
    archetype: string,
  ): TranslationSegment[] {
    switch (archetype) {
      case 'TRANSFER':
        return this.buildTransferSegments(node, graph, editable);
      case 'TRANSFORM':
        return this.buildTransformSegments(node, typeDef, graph, editable);
      case 'MEASURE':
        return this.buildMeasureSegments(node, typeDef, graph, editable);
      case 'COMPLEX':
        return this.buildComplexSegments(node, typeDef, graph, editable);
      default:
        return this.buildTransformSegments(node, typeDef, graph, editable);
    }
  }

  private static buildTransferSegments(
    node: OperationNodeData,
    graph: GraphSnapshotLight,
    editable: boolean,
  ): TranslationSegment[] {
    const segments: TranslationSegment[] = [];
    const inputMats = this.resolveInputMaterials(node.id, graph);
    const outputMats = this.resolveOutputMaterials(node.id, graph);

    segments.push({ kind: 'text', value: 'Transfer ' });

    // Quantity
    const qty = node.transferRow?.quantity;
    if (qty) {
      segments.push({
        kind: 'field',
        paramName: 'transferQuantity',
        value: qty.value.amount,
        unit: String(qty.value.units),
        fieldType: 'number',
        disabled: !editable,
      });
      segments.push({ kind: 'text', value: ` ${qty.value.units} ` });
    } else if (node.transferRow?.symbolic) {
      segments.push({ kind: 'text', value: `${node.transferRow.symbolic} ` });
    } else {
      segments.push({
        kind: 'field',
        paramName: 'transferQuantity',
        value: '',
        fieldType: 'number',
        disabled: !editable,
      });
      segments.push({ kind: 'text', value: ' ' });
    }

    segments.push({ kind: 'text', value: 'of ' });

    // Source material — get from graph input edges (material flowing into this operation)
    // The transferRow doesn't store source ID; it's inferred from edges
    const sourceSeg =
      (inputMats.length > 0 ? inputMats[0] : undefined);
    if (sourceSeg && sourceSeg.kind === 'material-ref') {
      segments.push({
        ...sourceSeg,
        showConstituentToggle: false,
        renderVariant: 'transfer-source',
      });
    } else if (sourceSeg) {
      segments.push(sourceSeg);
    }

    segments.push({ kind: 'text', value: ' to ' });

    // Destination — from transferRow.destinationMaterialId (where the material goes)
    const destSeg =
      this.materialRefById(
        node.transferRow?.destinationMaterialId,
        graph,
      );
    if (destSeg && destSeg.kind === 'material-ref') {
      segments.push({
        ...destSeg,
        renderVariant: 'transfer-destination',
      });
    } else if (destSeg) {
      segments.push(destSeg);
    }

    segments.push({ kind: 'text', value: '.' });
    return segments;
  }

  /** Build a material-ref segment from an explicit material id, if resolvable. */
  private static materialRefById(
    materialId: string | undefined,
    graph: GraphSnapshotLight,
  ): TranslationSegment | undefined {
    if (!materialId) return undefined;
    const mat = graph.materials.get(materialId);
    if (!mat) return undefined;
    return {
      kind: 'material-ref',
      materialName: mat.name,
      containerName: mat.containerName,
      constituentExpr: mat.constituentExpr,
      constituents: mat.constituents ? mat.constituents.map((c) => ({ ...c })) : [],
    };
  }

  private static buildTransformSegments(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
    graph: GraphSnapshotLight,
    editable: boolean,
  ): TranslationSegment[] {
    const segments: TranslationSegment[] = [];
    const opName = typeDef?.operationName ?? node.displayName;
    const inputMats = this.resolveInputMaterials(node.id, graph);

    segments.push({ kind: 'text', value: `Perform ${opName} on ` });

    if (inputMats.length > 0) {
      segments.push(inputMats[0]);
    }

    // Parameters
    const paramSegments = this.buildParameterSegments(node, typeDef, editable);
    if (paramSegments.length > 0) {
      segments.push({ kind: 'text', value: ' at ' });
      segments.push(...paramSegments);
    }

    const durationText = this.buildDurationText(node, typeDef);
    if (durationText) {
      segments.push({ kind: 'text', value: ` for ${durationText}` });
    }

    segments.push({ kind: 'text', value: '.' });
    return segments;
  }

  private static buildMeasureSegments(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
    graph: GraphSnapshotLight,
    editable: boolean,
  ): TranslationSegment[] {
    const segments: TranslationSegment[] = [];
    const opName = typeDef?.operationName ?? node.displayName;
    const inputMats = this.resolveInputMaterials(node.id, graph);

    segments.push({ kind: 'text', value: `Perform ${opName} on ` });

    if (inputMats.length > 0) {
      segments.push(inputMats[0]);
    }

    const paramSegments = this.buildParameterSegments(node, typeDef, editable);

    if (paramSegments.length > 0) {
      segments.push({ kind: 'text', value: ' with ' });
      segments.push(...paramSegments);
    }

    const durationText = this.buildDurationText(node, typeDef);
    if (durationText) {
      segments.push({ kind: 'text', value: ` for ${durationText}` });
    }

    segments.push({ kind: 'text', value: '.' });
    return segments;
  }

  private static buildComplexSegments(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
    graph: GraphSnapshotLight,
    editable: boolean,
  ): TranslationSegment[] {
    const opName = typeDef?.operationName ?? node.displayName;
    const inputMats = this.resolveInputMaterials(node.id, graph);
    const primaryInput = inputMats[0];
    const auxiliaryInputs = inputMats.slice(1);
    const paramSegments = this.buildParameterSegments(node, typeDef, editable);

    const durationText = this.buildDurationText(node, typeDef);

    const template = (typeDef?.translationTemplate ?? '').trim();
    const segments: TranslationSegment[] = [];

    if (template) {
      const tokens = template.split(/(\{operatorName\}|\{materialName\}|\{parameters\}|\{duration\})/gi);
      for (const token of tokens) {
        const lower = token.toLowerCase();
        if (lower === '{operatorname}') {
          segments.push({ kind: 'text', value: opName });
        } else if (lower === '{materialname}') {
          if (primaryInput) {
            segments.push(primaryInput);
          } else {
            segments.push({ kind: 'text', value: 'target material' });
          }
        } else if (lower === '{parameters}') {
          segments.push(...paramSegments);
        } else if (lower === '{duration}') {
          if (durationText) {
            segments.push({ kind: 'text', value: durationText });
          }
        } else if (token) {
          segments.push({ kind: 'text', value: token });
        }
      }
    } else {
      // Backward-compatible fallback when no template is defined.
      segments.push({ kind: 'text', value: `${opName} ` });
      if (primaryInput) {
        segments.push(primaryInput);
      }

      if (paramSegments.length > 0) {
        segments.push({ kind: 'text', value: ' ' });
        segments.push(...paramSegments);
      }

      if (durationText) {
        segments.push({ kind: 'text', value: ` for ${durationText}` });
      }
    }

    if (auxiliaryInputs.length > 0) {
      this.removeTrailingPeriod(segments);
      segments.push({ kind: 'text', value: auxiliaryInputs.length > 1 ? ' with auxiliary materials ' : ' with auxiliary material ' });
      auxiliaryInputs.forEach((mat, idx) => {
        if (idx > 0) {
          segments.push({ kind: 'text', value: ', ' });
        }
        segments.push(mat);
      });
    }

    this.ensureTrailingPeriod(segments);
    return segments;
  }

  private static removeTrailingPeriod(segments: TranslationSegment[]): void {
    const last = segments[segments.length - 1];
    if (!last || last.kind !== 'text') return;
    last.value = last.value.replace(/\s*\.\s*$/, '');
  }

  private static ensureTrailingPeriod(segments: TranslationSegment[]): void {
    const last = segments[segments.length - 1];
    if (last?.kind === 'text') {
      if (!last.value.trim().endsWith('.')) {
        last.value += '.';
      }
      return;
    }
    segments.push({ kind: 'text', value: '.' });
  }

  // ─── Parameter Helpers ─────────────────────────────────────────────────────

  private static buildParameterSegments(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
    editable: boolean,
  ): TranslationSegment[] {
    const segments: TranslationSegment[] = [];

    if (!typeDef) {
      // No catalog entry — render parameters from raw data
      const entries = Object.entries(node.parameters);
      entries.forEach(([key, val], i) => {
        if (i > 0) segments.push({ kind: 'text', value: ', ' });
        const fallbackTable = this.buildUnknownTableSegment(key, val, editable);
        if (fallbackTable) {
          segments.push({ kind: 'text', value: `${key}: ` });
          segments.push(fallbackTable);
        } else {
          segments.push({ kind: 'text', value: `${key} ` });
          segments.push({
            kind: 'field',
            paramName: key,
            value: val ?? '',
            fieldType: 'text',
            disabled: !editable,
          });
        }
      });
      return segments;
    }

    const orderedParams = [...typeDef.parameters].sort(
      (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
    );
    const consumedParamKeys = new Set<string>();

    let hasAny = false;

    for (const param of orderedParams) {
      consumedParamKeys.add(String(param.parameterName || '').toLowerCase());
      consumedParamKeys.add(String(param.id || '').toLowerCase());
      consumedParamKeys.add(String((param as any).safeKey || '').toLowerCase());
      if (['duration', 'Duration', 'actionType', 'inputNodes', 'outputNodes'].includes(param.parameterName)) {
        continue;
      }

      if (param.fieldType === 'table' || param.fieldType === 'gradientProgram') {
        const mandatory = isRequired(param.required);
        const tableSegment = this.buildTableSegment(node, param, editable, mandatory);
        if (!tableSegment) continue;
        if (hasAny) segments.push({ kind: 'text', value: ', ' });
        segments.push({ kind: 'text', value: `${param.parameterName}: ` });
        segments.push(tableSegment);
        hasAny = true;
        continue;
      }

      if (param.fieldType === 'group') {
        const childDefSource = (param as any).children ?? (param as any).paramValues ?? (param as any).subMenu ?? [];
        const childDefs = Array.isArray(childDefSource) ? childDefSource : [];

        const groupRaw = this.getParameterRawValue(node, param);
        let rows = pickGroupRows(groupRaw);

        if (rows.length === 0 && childDefs.length > 0) {
          rows = [{}];
        }

        let groupHasContent = false;
        const groupSegments: TranslationSegment[] = [];

        rows.forEach((row, rowIndex) => {
          let rowHasContent = false;
          if (rowIndex > 0 && groupHasContent) groupSegments.push({ kind: 'text', value: '; ' });
          const renderedKeys = new Set<string>();

          const rowObj = (row && typeof row === 'object' && !Array.isArray(row)) ? (row as Record<string, unknown>) : {};
          const nestedValueObj = (rowObj.value && typeof rowObj.value === 'object' && !Array.isArray(rowObj.value)) ? (rowObj.value as Record<string, unknown>) : null;
          const nestedParamsObj = (rowObj.parameters && typeof rowObj.parameters === 'object' && !Array.isArray(rowObj.parameters)) ? (rowObj.parameters as Record<string, unknown>) : null;
          const normalizedRow = (nestedValueObj || nestedParamsObj)
            ? { ...(nestedParamsObj ?? {}), ...(nestedValueObj ?? {}) }
            : rowObj;

          // Sort children by order field to maintain consistent ordering
          const children = [...childDefs].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

          // Filter children: show mandatory (even if empty) and optional with values
          const visibleChildren = children.filter((child: any) => {
            const rawValue =
              rowObj[child.parameterName] ??
              (child.safeKey ? rowObj[child.safeKey] : undefined) ??
              rowObj[child.id] ??
              this.getParameterRawValue(node, child);
            const normalizedRawValue =
              normalizedRow[child.parameterName] ??
              (child.safeKey ? normalizedRow[child.safeKey] : undefined) ??
              normalizedRow[child.id] ??
              rawValue;
            const isMandatory = isRequired(child.required) || this.inferMandatoryFromRaw(normalizedRawValue);
            const isEmpty = this.isEmptyLike(normalizedRawValue);
            return isMandatory || !isEmpty;
          });

          for (const child of visibleChildren) {
            const rawValue =
              rowObj[child.parameterName] ??
              (child.safeKey ? rowObj[child.safeKey] : undefined) ??
              rowObj[child.id] ??
              this.getParameterRawValue(node, child);

            const normalizedRawValue =
              normalizedRow[child.parameterName] ??
              (child.safeKey ? normalizedRow[child.safeKey] : undefined) ??
              normalizedRow[child.id] ??
              rawValue;

            const childSegs = buildScalarParamSegments(child, normalizedRawValue, { disabled: !editable, suppressUnitsPlaceholder: true });
            if (!childSegs) continue;

            if (rowHasContent) groupSegments.push({ kind: 'text', value: ', ' });
            groupSegments.push(...childSegs);
            rowHasContent = true;
            groupHasContent = true;
            if (child.parameterName) renderedKeys.add(String(child.parameterName));
            if ((child as any).safeKey) renderedKeys.add(String((child as any).safeKey));
            if (child.id) renderedKeys.add(String(child.id));
          }

          const fallbackEntries = Object.entries(normalizedRow)
            .filter(([k]) => !['parameters', 'value', 'units', 'required', 'mandatory', 'parameterId', 'parameterName', 'fieldName', 'name'].includes(k))
            .filter(([k]) => !renderedKeys.has(k))
            .map(([k, v]) => ({
              key: k,
              rawValue: v,
              normalizedValue: this.parseCanonicalValue(v),
              mandatory: this.inferMandatoryFromRaw(v),
            }))
            .filter((entry) => entry.mandatory || !this.isEmptyLike(entry.normalizedValue));


          for (const entry of fallbackEntries) {
            if (rowHasContent) groupSegments.push({ kind: 'text', value: ', ' });
            groupSegments.push({ kind: 'text', value: `${entry.key} ` });
            groupSegments.push({
              kind: 'field',
              paramName: `${param.parameterName}.${entry.key}`,
              value: entry.mandatory && this.isEmptyLike(entry.normalizedValue) ? '' : (entry.normalizedValue ?? ''),
              fieldType: typeof entry.normalizedValue === 'number' ? 'number' : 'text',
              disabled: !editable,
            });
            rowHasContent = true;
            groupHasContent = true;
          }
        });

        if (!groupHasContent && isRequired(param.required)) {
          groupSegments.push({
            kind: 'field',
            paramName: param.parameterName,
            value: '',
            fieldType: 'text',
            disabled: !editable,
          });
          groupHasContent = true;
        }

        if (!groupHasContent) continue;
        if (hasAny) segments.push({ kind: 'text', value: ', ' });
        segments.push({ kind: 'text', value: `${param.parameterName}: ` });
        segments.push(...groupSegments);
        hasAny = true;
        continue;
      }

      const rawValue = this.getParameterRawValue(node, param);
      const scalarSegs = buildScalarParamSegments(param, rawValue, { disabled: !editable, suppressUnitsPlaceholder: true });
      if (!scalarSegs) continue;
      if (hasAny) segments.push({ kind: 'text', value: ', ' });
      segments.push(...scalarSegs);
      hasAny = true;
    }

    const excluded = new Set(['duration', 'Duration', 'actionType', 'inputNodes', 'outputNodes']);
    const extraKeys = Object.keys(node.parameters)
      .filter((k) => !excluded.has(k))
      .filter((k) => !consumedParamKeys.has(String(k).toLowerCase()));

    for (const key of extraKeys) {
      const raw = node.parameters[key];
      const fallbackTable = this.buildUnknownTableSegment(key, raw, editable);
      if (fallbackTable) {
        if (hasAny) segments.push({ kind: 'text', value: ', ' });
        segments.push({ kind: 'text', value: `${key}: ` });
        segments.push(fallbackTable);
        hasAny = true;
        continue;
      }

      const mandatory = this.inferMandatoryFromRaw(raw);
      const canonical = this.parseCanonicalValue(raw);
      if (!mandatory && this.isEmptyLike(canonical)) continue;
      if (hasAny) segments.push({ kind: 'text', value: ', ' });
      segments.push({ kind: 'text', value: `${key} ` });
      segments.push({
        kind: 'field',
        paramName: key,
        value: mandatory && this.isEmptyLike(canonical) ? '' : (canonical ?? ''),
        fieldType: typeof canonical === 'number' ? 'number' : 'text',
        disabled: !editable,
      });
      hasAny = true;
    }

    return segments;
  }

  private static buildTableSegment(
    node: OperationNodeData,
    param: ParameterDefinition,
    editable: boolean,
    mandatory = false,
  ): TranslationSegment | null {
    const rawTable = this.getParameterRawValue(node, param);
    const obj = (rawTable && typeof rawTable === 'object' ? rawTable : {}) as Record<string, unknown>;

    let sourceRows: Array<Record<string, unknown>> = [];
    let columns: TableColumn[] = [];

    if (param.fieldType === 'gradientProgram') {
      // Gradient rows may be stored at param key as a nested obj, OR flat in node.parameters
      // under the canonical keys 'GradientProgram' (array) and 'totalPumps' (number).
      const flatRows = node.parameters['GradientProgram'] as Array<Record<string, unknown>> | undefined;
      const flatTotalPumps = node.parameters['totalPumps'] as number | undefined;

      // Use || not ?? — || skips both null/undefined AND empty arrays []
      const resolvedRows = pickRowArray(rawTable);
      const candidateRows =
        (resolvedRows.length > 0 ? resolvedRows : null) ||
        (Array.isArray(flatRows) && flatRows.length > 0 ? flatRows : null) ||
        null;

      // candidateRows is null when no data entered; we still render one empty row below
      sourceRows = (candidateRows ?? []) as Array<Record<string, unknown>>;

      // Helper to extract scalar value from wrapped structures
      const getScalarValue = (val: any): any => {
        if (val === null || val === undefined) return undefined;
        if (typeof val === 'object' && !Array.isArray(val) && 'value' in val) {
          return val.value;
        }
        return val;
      };

      // Comprehensive pump count resolution:
      // 1. Try direct property
      // 2. Try wrapped value property
      // 3. Try flat node parameter
      // 4. Try wrapped flat parameter
      // 5. Try from rows data (if rows exist, infer from keys)
      // 6. Fall back to param definition or 2
      let pumpCount: number = 2;
      
      if (typeof obj.totalPumps === 'number' && obj.totalPumps > 0) {
        pumpCount = obj.totalPumps;
      } else if (typeof getScalarValue(obj.totalPumps) === 'number' && getScalarValue(obj.totalPumps) > 0) {
        pumpCount = getScalarValue(obj.totalPumps);
      } else if (typeof (obj.value as any)?.totalPumps === 'number' && (obj.value as any)?.totalPumps > 0) {
        pumpCount = (obj.value as any).totalPumps;
      } else if (typeof flatTotalPumps === 'number' && flatTotalPumps > 0) {
        pumpCount = flatTotalPumps;
      } else if (typeof getScalarValue(flatTotalPumps) === 'number' && getScalarValue(flatTotalPumps) > 0) {
        pumpCount = getScalarValue(flatTotalPumps);
      } else if (Array.isArray(sourceRows) && sourceRows.length > 0) {
        // Infer from actual row data: count Pump* columns in first row
        const firstRow = sourceRows[0];
        const pumpKeys = Object.keys(firstRow).filter((k) => k.startsWith('Pump'));
        if (pumpKeys.length > 0) {
          pumpCount = pumpKeys.length;
        } else {
          // Fallback: use param definition or default
          pumpCount = param.pumpCount ?? 2;
        }
      } else {
        // No rows to infer from, use param definition
        pumpCount = param.pumpCount ?? 2;
      }

      columns = [{ key: 'Time', label: 'Time' }];
      for (let i = 0; i < pumpCount; i++) {
        const suffix = String.fromCharCode(65 + i);
        columns.push({ key: `Pump${suffix}`, label: `Pump${suffix}` });
      }
      columns.push({ key: 'Conditions', label: 'Conditions' });
    } else {
      sourceRows = pickRowArray(rawTable);

      columns = (param.tableColumns ?? []).map((col) => ({
        key: col.id,
        label: col.header,
      }));

      if (columns.length > 0) {
        columns = normalizeTableColumns((param.tableColumns ?? []).map((col) => ({
          id: col.id,
          header: col.header,
          required: col.required,
        })));
      }

      if (columns.length === 0 && sourceRows.length > 0) {
        columns = Object.keys(sourceRows[0]).map((key) => ({ key, label: key }));
      }
    }

    if (columns.length === 0) return null;

    const filledRows = sourceRows.filter(rowHasValue);
    // Hide non-mandatory empty tables (including gradient) to align with add-step preview behavior.
    if (filledRows.length === 0 && !mandatory) return null;

    // Always show at least one empty row so the table body is never blank
    const effectiveRows = filledRows.length > 0 ? filledRows : [{}];

    const rows: TableRow[] = effectiveRows.map((raw) => {
      const row: TableRow = {};
      for (const col of columns) {
        const rawValue = raw[col.key] ?? raw[col.label];
        let value = this.parseCanonicalValue(rawValue);
        
        // Ensure value is a scalar (string, number, boolean) not an object
        if (value && typeof value === 'object') {
          value = '';
        }
        
        // Convert to string if needed, handle null/undefined
        const stringValue = value == null ? '' : String(value);
        
        row[col.key] = {
          value: stringValue,
          fieldType: typeof value === 'number' ? 'number' : 'text',
        };
      }
      return row;
    });

    return {
      kind: 'table',
      paramName: param.parameterName,
      // paramName: param.id,  // use parameterName for consistency with add-step
      columns,
      rows,
      disabled: !editable,
    };
  }

  private static buildUnknownTableSegment(
    key: string,
    raw: unknown,
    editable: boolean,
  ): TranslationSegment | null {
    const rows = pickRowArray(raw).filter((row) => rowHasValue(row));
    if (rows.length === 0) return null;

    const columnKeys: string[] = [];
    rows.forEach((row) => {
      Object.keys(row).forEach((col) => {
        if (!columnKeys.includes(col)) columnKeys.push(col);
      });
    });
    if (columnKeys.length === 0) return null;

    const columns: TableColumn[] = columnKeys.map((col) => ({ key: col, label: col }));
    const tableRows: TableRow[] = rows.map((row) => {
      const mapped: TableRow = {};
      columns.forEach((col) => {
        const value = this.parseCanonicalValue(row[col.key]);
        mapped[col.key] = {
          value: value ?? '',
          fieldType: typeof value === 'number' ? 'number' : 'text',
        };
      });
      return mapped;
    });

    return {
      kind: 'table',
      paramName: key,
      columns,
      rows: tableRows,
      disabled: !editable,
    };
  }

  // ─── Material Resolution ──────────────────────────────────────────────────

  private static resolveInputMaterials(
    operationId: string,
    graph: GraphSnapshotLight,
  ): TranslationSegment[] {
    const inputIds = graph.inputMaterials.get(operationId) ?? [];
    const results: TranslationSegment[] = [];
    for (const matId of inputIds) {
      const mat = graph.materials.get(matId);
      if (mat) {
        results.push({
          kind: 'material-ref',
          materialName: mat.name,
          containerName: mat.containerName,
          constituentExpr: mat.constituentExpr,
          constituents: mat.constituents ? mat.constituents.map((c) => ({ ...c })) : [],
        });
      }
    }
    return results;
  }

  private static resolveOutputMaterials(
    operationId: string,
    graph: GraphSnapshotLight,
  ): TranslationSegment[] {
    const outputIds = graph.outputMaterials.get(operationId) ?? [];
    const results: TranslationSegment[] = [];
    for (const matId of outputIds) {
      const mat = graph.materials.get(matId);
      if (mat) {
        results.push({
          kind: 'material-ref',
          materialName: mat.name,
          containerName: mat.containerName,
          constituentExpr: mat.constituentExpr,
          constituents: mat.constituents ? mat.constituents.map((c) => ({ ...c })) : [],
        });
      }
    }
    return results;
  }

  // ─── Checkpoints ──────────────────────────────────────────────────────────

  private static buildCheckpoints(node: OperationNodeData, editable: boolean): CheckpointDef[] {
    const mapCheckpoint = (cp: Checkpoint): CheckpointDef => ({
      id: cp.id,
      name: cp.checkpointName,
      fieldType: cp.fieldType,
      mandatory: cp.required,
      disabled: !editable,
      value: (cp as Checkpoint & { value?: unknown }).value,
      acceptanceMin: cp.rangeMin,
      acceptanceMax: cp.rangeMax,
      allowMultiple: cp.allowMultiple,
      allowSpecialCharacters: cp.allowSpecialCharacters,
      uniqueAcrossWorkspace: cp.uniqueAcrossWorkspace,
      linkMultiple: cp.linkMultiple,
      unitSource: cp.unitSource,
      unitCategory: cp.unitCategory,
      units: cp.units,
      allowedEntityTypes: cp.allowedEntityTypes,
      dropdown: cp.dropdown?.map((d) => ({ label: d.label, id: d.id })),
      referenceFile: cp.referenceFile,
      referenceSpreadsheetFile: cp.referenceSpreadsheetFile,
      children: cp.children?.map(mapCheckpoint),
    });

    return (node.checkPoints ?? []).map(mapCheckpoint);
  }

  // ─── Duration ─────────────────────────────────────────────────────────────

  private static buildDurationText(
    node: OperationNodeData,
    typeDef: OperationTypeDefinition | undefined,
  ): string | undefined {
    if (!node.duration || node.duration.isInfinity) return undefined;
    if (typeDef?.durationAllow === false) return undefined;

    const seconds =
      typeof node.duration.value === 'number' && Number.isFinite(node.duration.value)
        ? Math.max(0, Math.floor(node.duration.value))
        : 0;

    const durationRequired = typeDef?.durationRequired === true;

    // Non-mandatory duration should stay hidden when no value is provided.
    if (!durationRequired && seconds === 0) {
      return undefined;
    }

    // Mandatory duration explicitly shows 0 sec when left at zero.
    if (seconds === 0) {
      return '0 sec';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  private static resolveDuration(node: OperationNodeData): StepTranslation['duration'] {
    if (!node.duration || node.duration.isInfinity || node.duration.value == null) return undefined;
    return { value: node.duration.value, unit: 'sec' };
  }

  // ─── Icons ────────────────────────────────────────────────────────────────

  private static buildIcons(node: OperationNodeData): StepIconState {
    const notesText = typeof node.notes === 'string' ? node.notes.trim() : '';
    return {
      hasNotes: notesText.length > 0,
      hasApproval: Boolean(node.approvers?.users?.length),
      hasNotifications: Boolean(node.notifications?.users?.length),
    };
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  private static parseCanonicalValue(raw: unknown): unknown {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      
      // Priority extraction: label → value → amount → input
      if ('label' in obj && obj.label != null && obj.label !== '') return obj.label;
      if ('value' in obj && obj.value != null && obj.value !== '') return this.parseCanonicalValue(obj.value);
      if ('amount' in obj && obj.amount != null && obj.amount !== '') return this.parseCanonicalValue(obj.amount);
      if ('input' in obj && obj.input != null && obj.input !== '') return this.parseCanonicalValue(obj.input);
      
      // If object has only display-like properties, try to construct a string
      if ('display' in obj) return this.parseCanonicalValue(obj.display);
      if ('text' in obj) return this.parseCanonicalValue(obj.text);
      if ('name' in obj) return this.parseCanonicalValue(obj.name);
      
      // If it's a small object with numeric keys (like array-like), don't return [object Object]
      const keys = Object.keys(obj);
      if (keys.length === 0) return '';
      
      // If object is not one of the known shapes, return empty string instead of [object Object]
      // This prevents "[object Object]" from appearing in UI
      return '';
    }
    if (Array.isArray(raw)) return '';
    return raw;
  }

  private static isEmptyLike(raw: unknown): boolean {
    if (raw == null) return true;
    if (typeof raw === 'string') return raw.trim() === '';
    if (typeof raw === 'number') return false;
    if (Array.isArray(raw)) return raw.length === 0;
    if (typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      if ('value' in obj) return this.isEmptyLike(obj.value);
      if ('amount' in obj) return this.isEmptyLike(obj.amount);
      return Object.values(obj).every((v) => this.isEmptyLike(v));
    }
    return false;
  }

  private static inferMandatoryFromRaw(value: unknown, depth = 0): boolean {
    if (depth > 4 || value == null || typeof value !== 'object') return false;
    if (Array.isArray(value)) return value.some((v) => this.inferMandatoryFromRaw(v, depth + 1));
    const obj = value as Record<string, unknown>;
    const req = obj.required;
    const mand = obj.mandatory;
    if (isRequired(req) || isRequired(mand) || obj.coreField === true) return true;
    if ('value' in obj && this.inferMandatoryFromRaw(obj.value, depth + 1)) return true;
    if ('parameters' in obj && this.inferMandatoryFromRaw(obj.parameters, depth + 1)) return true;
    return false;
  }

  private static getParameterRawValue(
    node: OperationNodeData,
    param: ParameterDefinition,
  ): unknown {
    const paramName = String(param.parameterName || '');
    const id = String(param.id || '');
    const safeKey = String((param as any).safeKey || '');

    const caseInsensitiveLookup = (target: string): unknown => {
      if (!target) return undefined;
      const foundKey = Object.keys(node.parameters).find((k) => k.toLowerCase() === target.toLowerCase());
      return foundKey ? node.parameters[foundKey] : undefined;
    };

    return (
      node.parameters[paramName] ??
      node.parameters[id] ??
      ((param as any).safeKey ? node.parameters[(param as any).safeKey] : undefined) ??
      caseInsensitiveLookup(paramName) ??
      caseInsensitiveLookup(id) ??
      caseInsensitiveLookup(safeKey)
    );
  }

}
